import type { SimulatorItem } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import cn from 'classnames'
import { useCallback, useEffect } from 'react'
import React from 'react'
import { FileTypeEnum } from 'utils'

import FloatingPanel from 'components/Editor/FloatingPanel'
import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import { chainName, FormField, FieldFile, FieldInput, FieldColorPicker } from 'components/formKit'
import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import Button, { IconButton, TileButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import ScrollService, { scrollProps } from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getActiveElementId, getElementState } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { getFileByUsage } from 'utils/files'

import * as s from './SimulatorFormValueItem.module.scss'
import UnlinkedIcon from './assets/subtract.svg?react'
import type { SimulatorState } from './simulatorTypes'

const LABEL = genElementValueField({
  as: FieldInput,
  name: 'label',
  props: { placeholder: t('elements.simulator.item') },
})

const IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'image',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
  },
})

const OVERLAY = genElementValueField({
  as: FieldColorPicker<string | null>,
  name: 'overlay',
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})

export const SimulatorFormValueItem: React.FC<IRenderItemProps<SimulatorItem>> = ({
  value: array,
  index,
  item,
  name,
  min,
  isDragging,
  drag,
  onChange,
}) => {
  const disableDelete = Boolean(min && array.length <= min)
  const file = getFileByUsage(item.image, FileUsageTypeEnum.image)
  const hasLinkedSlide = item.areas?.some((area) => area.itemId)

  const dispatch = useProjectDispatch()
  const elementId = useProjectContext(getActiveElementId)
  const state = useProjectContext(getElementState, elementId || '')
  const isActive = item.value === state?.active

  const setState = useCallback(
    (value: SimulatorState) => {
      if (elementId) {
        dispatch(setElement({ id: elementId, value }))
      }
    },
    [dispatch, elementId],
  )

  const handleFocus = useCallback(
    () => setState({ ...state, active: item.value }),
    [setState, state, item.value],
  )

  const onDelete = useCallback(() => {
    return onChange?.(
      array.filter((_item, i) => i !== index),
      { command: 'delete', item: array[index] },
    )
  }, [onChange, array, index])

  useEffect(() => {
    if (isActive) {
      ScrollService.scroll(
        { container: ScrollContainerEnum.toolbar, id: item.value, async: 200 },
        null,
      )
    }
  }, [isActive, item.value])

  return (
    <Button
      name={`simulator-item-${index}`}
      styleType='free'
      {...scrollProps(item.value)}
      className={cn(s.root, { [s.active]: !isDragging && isActive })}
      onClick={handleFocus}
      onMouseDown={handleFocus}
    >
      <div ref={drag}>
        <div className={s.header}>
          <div className={s.title}>
            <Icon name='builderDrugAndDrop' size={KitSize.S} />
            <Typography indents={{ marginRight: 8 }}>
              {t('elements.simulator.form.item')} {index + 1}
            </Typography>
            {!hasLinkedSlide && (
              <Tooltip overlay={t('elements.simulator.tooltip.unlinked')}>
                <UnlinkedIcon />
              </Tooltip>
            )}
          </div>
          <div className={s.actions}>
            <Tooltip overlay={t('uiKit.tooltip.delete')}>
              <IconButton
                disabled={disableDelete}
                icon='otherTrash'
                name='delete'
                onClick={onDelete}
                size={KitSize.XS}
                styleType='ghost'
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <FloatingPanel
        header={<Typography weight='medium'>{t('page.finder.tag.image')}</Typography>}
        panel={
          <Padding gap>
            <FormField config={chainName(name, `${index}`, LABEL)} />
            <Typography weight='medium'>{t('input.label.image')}</Typography>
            <FormField config={chainName(name, `${index}`, IMAGE)} />
            <FormField config={chainName(name, `${index}`, OVERLAY)} />
          </Padding>
        }
      >
        {({ open }) => (
          <TileButton active={open} icon='otherAdd' name='settings' styleType='ghost' fluid>
            <PanelIconImage file={item.image} icon='otherBlock' />
            <Typography rows={1} styleType='hint' tooltip={file?.name}>
              {item.label || t('elements.simulator.item')}
            </Typography>
          </TileButton>
        )}
      </FloatingPanel>
    </Button>
  )
}

export default React.memo(SimulatorFormValueItem) as typeof SimulatorFormValueItem
